import { inject, Injectable, TemplateRef } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogActionType, DialogData } from 'src/app/api/model/dialogData';
import { AppResource } from 'src/app/app.resource';
import { VyvEmptyPopupComponent } from 'src/app/shared/components/vyv-empty-popup/vyv-empty-popup.component';
import { VyvPopupComponent, VyvPopupConfig } from 'src/app/shared/components/vyv-popup/vyv-popup.component';
import { environment } from 'src/environments/environment';

export enum DialogButtonType {
  VALIDATION = 'validation',
  CANCEL = 'cancel',
  CUSTOM = 'custom',
}

export interface VyvCustomDialogButton {
  type: DialogButtonType;
  text: string;
  action: () => void;
  icon?: string;
  cssClass?: string;
}

export interface VyvCustomDialogConfig {
  title: string;
  template: TemplateRef<void>;
  actionText?: string;
  onValidate?: () => void;
  onCancel?: () => void;
  noDefaultAction?: boolean; /** Si true, n'affiche aucun bouton par défaut (gérer les actions dans le template) */
}
@Injectable({ providedIn: 'root' })
export class VyvDialogService {
  resource = inject(AppResource)['resource'];
  private store = inject(AppResource)['resource'];
  constructor(private dialogService: DialogService) {}

  /**
   * Ouvre une modale personnalisée avec le style VYV
   * @param config Configuration de la modale avec template personnalisé
   * @returns Référence à la modale ouverte
   */
  openCustomDialog(config: VyvCustomDialogConfig): DynamicDialogRef {
    let actions = config.noDefaultAction
      ? []
      : [
          {
            type: DialogActionType.CONFIRM,
            text: config.actionText || 'Continuer',
          },
        ];

    actions = config.onCancel
      ? [
          {
            type: DialogActionType.CANCEL,
            text: 'Annuler',
          },
          ...actions,
        ]
      : actions;

    const dialogData: DialogData = {
      isCustom: true,
      title: config.title,
      showTitle: !!config.title,
      contentTemplate: config.template,
      actions: actions,
    };

    const ref = this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: dialogData,
    });

    if (config.onValidate) {
      ref.onClose.subscribe((result) => {
        if (result && config.onValidate) {
          config.onValidate();
        } else if (!result && config.onCancel) {
          config.onCancel();
        }
      });
    }

    return ref;
  }

  /**
   * Ouvre une modale de message simple avec le style VYV
   * @param title Titre de la modale
   * @param message Message à afficher
   * @param isError Indique si le message est une erreur
   * @returns Référence à la modale ouverte
   */
  openErrorDialogApi(): DynamicDialogRef {
    const dialogData: DialogData = {
      isCustom: true,
      title: this.resource.error.oups,
      showTitle: true,
      content: [this.resource.error.api],
      actions: [
        {
          type: DialogActionType.CONFIRM,
          text: 'OK',
        },
      ],
    };

    return this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: dialogData,
    });
  }

  openClimaticRiskDialog(content: string): DynamicDialogRef {
    const baseContent =
      this.resource.confirmationPopup.content.climaticRiskInfo +
      '<h2 class="font-bold text-xl">' +
      this.store.phone +
      '</h2>' +
      '<br/>' +
      this.resource.error.workflowOut.text2 +
      this.resource.error.workflowOut.text3;

    const dialogData: DialogData = {
      isCustom: true,
      title: this.resource.error.workflowOut.title,
      showTitle: true,
      content: [content, baseContent],
      actions: [
        {
          type: DialogActionType.CONFIRM,
          text: 'Compris',
        },
      ],
    };
    return this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: dialogData,
    });
  }

  openLoadingDialog(isLoading: boolean, message: string): DynamicDialogRef {
    let lottie: string = `assets/${environment.brand}/lotties/edition_document.json`;
    if (isLoading) {
      lottie = `assets/${environment.brand}/lotties/loading.json`;
    }

    const messageContent = message ?? this.resource.quote_confirmation.quotationCreation;
    return this.dialogService.open(VyvEmptyPopupComponent, {
      ...VyvPopupConfig,
      data: {
        lottiePath: lottie,
        content: [messageContent],
      } as Partial<DialogData>,
    });
  }
}
